import { FC, ReactNode, useState } from 'react';
import { Dropdown, Menu } from 'antd';
import { TbColumns, TbSearch } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { DropdownMenuLabel, DropdownOptionContent, DropdownPanel, DropdownPanelMenu, EmptySection } from '@/shared';

interface ColumnDropdownFiltersProps {
    children?: ReactNode;
    handleMenuClick: (index: number, key: string) => void;
    listColumns?: string[];
    col: string;
    index: number;
    availableColumns: string[];
    columnLabels?: Record<string, string>;
}

const ColumnDropdownFilters: FC<ColumnDropdownFiltersProps> = ({
    children,
    col,
    index,
    handleMenuClick,
    listColumns,
    availableColumns,
    columnLabels,
}): JSX.Element => {
    const [input, setInput] = useState('');

    const filteredColumns = availableColumns.filter((key: string) => key.toLowerCase().includes(input.toLowerCase()));

    const { token } = useAntDesignTheme();

    const endpointToLabelMap: Record<string, string> = {
        symbol: 'Ticker Symbol',
        price: 'Price per Share',
        beta: 'Beta',
        volAvg: 'Average Volume per Trading Day',
        mktCap: 'Market Cap',
        lastDiv: 'Last Dividend',
        companyName: 'Company Name',
        cik: 'CIK Code',
        isin: 'ISIN Code',
        cusip: 'CUSIP Code',
        exchange: 'Exchange (Full)',
        // exchange: 'Exchange (Shortended)',
        industry: 'Industry',
        website: 'Website',
        description: 'Description',
        ceo: 'CEO',
        sector: 'Sector',
        country: 'Country',
        fullTimeEmployees: 'Employee Headcount',
        phone: 'Phone Number',
        address: 'Headquarters Address',
        city: 'City',
        state: 'State / Territory',
        zip: 'ZIP Code',
        ipoDate: 'Date of IPO',
        industryGroup: 'Industry Group',
        subIndustry: 'Sub-Industry',
        gicsCode: 'GICS Code',
        naicsCode: 'NAICS Code',
        tags: 'Tags',
        foundedYear: 'Founding Year',
        timeZone: 'Timezone',
        facebook: 'Facebook URL',
        linkedin: 'LinkedIn URL',
        twitter: 'Twitter URL',
        crunchbase: 'Crunchbase URL',
        trafficRank: 'Website Traffic',
        raised: 'Fundraised',
        fiscalYearEnd: 'Fiscal Year-End',
        tech: 'Technologies',
        techCategories: 'Technology Categories',
    };

    return (
        <Dropdown
            placement='bottomRight'
            trigger={['click']}
            popupRender={() => (
                <DropdownPanel
                    maxHeight={300}
                    width={280}
                    search={{
                        placeholder: 'Filter Columns...',
                        setValue: setInput,
                        value: input,
                    }}
                >
                    <DropdownMenuLabel icon={<TbColumns size='0.875rem' />}>Options</DropdownMenuLabel>
                    {filteredColumns.length ? (
                        <DropdownPanelMenu
                            selectedKeys={[col]}
                            onClick={({ key }) => {
                                handleMenuClick(index, key);
                            }}
                        >
                            {filteredColumns.map((column) => {
                                return (
                                    <Menu.Item key={column} disabled={listColumns?.includes(column)}>
                                        <DropdownOptionContent
                                            control={<TbColumns size='1rem' />}
                                            selected={col === column}
                                            title={columnLabels?.[column] ?? endpointToLabelMap[column] ?? column}
                                        />
                                    </Menu.Item>
                                );
                            })}
                        </DropdownPanelMenu>
                    ) : (
                        <EmptySection
                            height='10rem'
                            icon={<TbSearch size='75%' style={{ color: token.colorTextQuaternary }} />}
                            label='No Matching Columns'
                            description={
                                input ? (
                                    <>
                                        No columns match <strong>{input}</strong>.
                                    </>
                                ) : (
                                    'No columns are available for this table.'
                                )
                            }
                        />
                    )}
                </DropdownPanel>
            )}
        >
            {children}
        </Dropdown>
    );
};

export default ColumnDropdownFilters;
